<template>
    <div class="slice barometer-job__cities">
        <div class="slice-content joy-pb-4">
            <h2 class="slice-title">{{ title }}</h2>
            <p>{{ subtitle }}</p>

            <table class="slice-body barometer-job__cities__list text-center">
                <thead>
                    <tr>
                        <th id="header-job text-left" width="1">
                            {{ t('barometer.family.average.price.cities.table.speciality.label') }}
                        </th>
                        <th id="header-price" class="highlight text-center" width="1" :style="{width: '15%'}">
                            {{ t('barometer.family.average.price.cities.table.average.price.label') }}
                        </th>
                        <th
                            v-for="(locality, i) in jobBarometer.localities"
                            :id="`header-city-${i}`"
                            :key="locality"
                            class="text-center"
                            :style="{width: '10%'}"
                        >
                            {{ locality }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(jobAndPrice, i) in jobAndPrices" :key="i" class="barometer-job__city" :class="{hidden: i >= nbShown && !showAll}">
                        <th :id="`header-job-${i}`" class="text-left">
                            <NuxtLink :to="jobAndPrice.url" class="barometer-job__city__label stretched-link">
                                {{ jobAndPrice.label }}
                            </NuxtLink>
                        </th>
                        <td
                            class="highlight text-center cell"
                            :headers="`header-job-${i} header-price`"
                            :data-info="t('barometer.family.average.price.cities.table.average.price.label')"
                        >
                            {{ formatPrice(jobAndPrice.price) }}
                        </td>
                        <td
                            v-for="(cityAndPrice, j) in jobAndPrice.cityAndPrices"
                            :key="j"
                            :headers="`header-job-${i} header-city-${j}`"
                            :data-info="`${cityAndPrice.city}`"
                            class="text-center"
                        >
                            {{ getDisplayPrice(cityAndPrice.price, cityAndPrice.count > 10) }}
                        </td>
                    </tr>
                </tbody>
            </table>

            <div v-if="!showAll && jobAndPrices.length > nbShown" class="joy-mt-10">
                <VJoyButton variant="secondary" @click="showAll = true">{{ t('generic.show.more') }}</VJoyButton>
            </div>

            <div class="joy-mt-10 joy-font-size-primary-200">* {{ t('barometer.family.average.price.cities.limited.representativity.label') }}</div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {inject, ref} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import {currenciesHelper} from '~/composables/currenciesHelper';
    import {BAROMETER_JOB} from '~/keys';

    const {jobBarometer} = inject(BAROMETER_JOB)!;
    const {formatPrice} = currenciesHelper();
    const {t} = useTranslation();

    const getDisplayPrice = (price: number, limited?: boolean) => {
        const value = price ? formatPrice(price) : 0;
        const suffix = limited ? ' *' : '';
        return value ? `${value}${suffix}` : '-';
    };

    const showAll = ref(false);
    const nbShown = 4;
    const htmlTitle = t('barometer.family.average.price.cities.title', [jobBarometer.country]);
    const [title, subtitle] = htmlTitle.match(/>([^<]+)</g)?.map((match) => match.slice(1, -1).trim()) || [];

    const jobAndPrices = jobBarometer.jobAndPrices.filter((jobAndPrice) => jobAndPrice.price > 0).sort((a, b) => b.price - a.price);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .barometer-job__cities {
        &__list {
            width: 100%;
            text-align: left;
            border-collapse: separate;

            th,
            td {
                padding: var(--joy-core-spacing-4);
                border: 1px solid transparent;
            }
            th {
                font-weight: var(--joy-font-weight-bold);
            }
            tbody {
                tr {
                    position: relative;
                }
                td {
                    white-space: nowrap;
                }
            }
            a {
                color: var(--joy-color-text-body);
                &:hover {
                    text-decoration: underline;
                }
            }
            .hidden {
                display: none;
            }
            // mobile only
            @include mq.screen_xs() {
                thead th {
                    display: none;
                }
                tbody th {
                    background-color: var(--joy-color-neutral-20);
                    margin-top: var(--joy-core-spacing-4);
                    border-top-color: var(--joy-color-neutral-30);
                }
                td:nth-child(odd) {
                    background-color: var(--joy-color-neutral-20);
                }
                td:last-child {
                    border-bottom-color: var(--joy-color-neutral-30);
                }
                td::before {
                    content: attr(data-info);
                }
                th,
                td {
                    display: flex;
                    justify-content: space-between;
                    border-left: 1px solid var(--joy-color-neutral-30);
                    border-right: 1px solid var(--joy-color-neutral-30);
                }
            }

            // desktop only
            @include mq.screen_sm(min) {
                th,
                td {
                    border-bottom-color: var(--joy-color-neutral-30);
                }
                tr:last-child {
                    th,
                    td {
                        border-bottom-color: transparent;
                    }
                }
                .highlight {
                    background-color: var(--joy-color-neutral-20) !important;
                    border-left: 1px solid var(--joy-color-neutral-30);
                    border-right: 1px solid var(--joy-color-neutral-30);
                }
                th.highlight {
                    border-top: 1px solid var(--joy-color-neutral-30);
                }
                tbody tr:nth-child(odd) > * {
                    background-color: var(--joy-color-neutral-20);
                }

                // hover
                tbody tr:hover {
                    th,
                    td {
                        background-color: var(--joy-color-secondary-10) !important;
                    }
                }
            }
        }
    }
</style>
